import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Chip,
  ButtonGroup,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import PrintIcon from '@material-ui/icons/Print';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ReceiptIcon from '@material-ui/icons/Receipt';
import DescriptionIcon from '@material-ui/icons/Description';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { useCookies } from 'react-cookie';
import { useReactToPrint } from 'react-to-print';
import {
  CashierThermalTemplate,
  CashierA4Template,
} from './CashierPrintTemplates';
import { formattedCurrency } from '../../../utils/utils';
import './CashierPrintModal.css';

const CashierPrintPreviewModal = ({
  open,
  handleClose,
  cashier,
  closure,
  movesData,
  paymentMethods = [],
  credencials = {},
  loading = false,
  onRefresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerRef = useRef(null);
  const a4ContentRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [a4Height, setA4Height] = useState(0);
  const [zoomMode, setZoomMode] = useState('auto'); // 'auto', 0.5, 1.0

  const [cookies, setCookie] = useCookies([
    'cashier_print_type',
    'cashier_print_mode',
  ]);

  const [printType, setPrintType] = useState(
    cookies.cashier_print_type || 'thermal',
  );
  const [printMode, setPrintMode] = useState(
    cookies.cashier_print_mode || 'agrupado',
  );

  const printRef = useRef();

  useEffect(() => {
    if (cookies.cashier_print_type) {
      setPrintType(cookies.cashier_print_type);
    }
  }, [cookies.cashier_print_type]);

  useEffect(() => {
    if (cookies.cashier_print_mode) {
      setPrintMode(cookies.cashier_print_mode);
    }
  }, [cookies.cashier_print_mode]);

  const handlePrintTypeChange = (event) => {
    const value = event.target.value;
    setPrintType(value);
    setCookie('cashier_print_type', value, { path: '/', maxAge: 31536000 });
  };

  const handlePrintModeChange = (event) => {
    const value = event.target.value;
    setPrintMode(value);
    setCookie('cashier_print_mode', value, { path: '/', maxAge: 31536000 });
  };

  // Medição da largura disponível do container para cálculo do zoom auto-fit
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    update();
    const timer = setTimeout(update, 120);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', update);
    };
  }, [open, printType, isMobile]);

  // Medição da altura real do template A4
  useEffect(() => {
    if (!open || printType !== 'a4') return;
    const measure = () => {
      if (a4ContentRef.current) {
        setA4Height(a4ContentRef.current.offsetHeight);
      }
    };
    measure();
    const timer = setTimeout(measure, 150);
    return () => clearTimeout(timer);
  }, [open, printType, printMode, movesData]);

  // Cálculo da escala de preview A4
  const a4Scale = useMemo(() => {
    if (printType !== 'a4') return 1;
    if (typeof zoomMode === 'number') return zoomMode;
    // Auto fit width
    const targetWidth =
      containerWidth > 0
        ? containerWidth - (isMobile ? 12 : 32)
        : isMobile
        ? 310
        : 640;
    const calculated = targetWidth / 794;
    return Math.min(Math.max(calculated, 0.35), 0.85);
  }, [printType, zoomMode, containerWidth, isMobile]);

  const thermalPageStyle = `
    @page { size: 80mm auto; margin: 0; }
    @media print {
      body { margin: 0; padding: 0; width: 80mm; }
      body * { visibility: visible !important; }
      table { width: 100% !important; border-collapse: collapse !important; }
      .MuiTableCell-root { border-bottom: 1px dashed #ccc !important; padding: 3px 0 !important; }
    }
  `;

  const a4PageStyle = `
    @page { size: A4; margin: 10mm; }
    @media print {
      body { margin: 0; padding: 0; width: 100%; }
      body * { visibility: visible !important; }
      table { width: 100% !important; border-collapse: collapse !important; }
      th { background-color: #f1f5f9 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      td, th { border: 1px solid #cbd5e1 !important; padding: 6px 8px !important; }
    }
  `;

  const handlePrintFn = useReactToPrint({
    contentRef: printRef,
    pageStyle: printType === 'thermal' ? thermalPageStyle : a4PageStyle,
    onAfterPrint: () => {
      const printIframe = document.getElementById('printWindow');
      if (printIframe) printIframe.remove();
      handleClose();
    },
  });

  const handlePrint = () => {
    const oldIframe = document.getElementById('printWindow');
    if (oldIframe) oldIframe.remove();

    const oldClone = document.getElementById('safari-cashier-print-clone');
    if (oldClone) oldClone.remove();

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isSafari || isIOS) {
      const wrapperClass =
        printType === 'thermal'
          ? 'cashier-print-wrapper-thermal'
          : 'cashier-print-wrapper-a4';
      const clone = printRef.current.cloneNode(true);
      clone.className = wrapperClass;
      clone.setAttribute('id', 'safari-cashier-print-clone');
      document.body.appendChild(clone);

      const cleanupClone = () => {
        const el = document.getElementById('safari-cashier-print-clone');
        if (el) el.remove();
        window.removeEventListener('afterprint', cleanupClone);
      };
      window.addEventListener('afterprint', cleanupClone);

      setTimeout(() => {
        window.print();
      }, 300);
    } else {
      handlePrintFn();
    }
  };

  const entriesCount = movesData?.entries?.moves?.length || 0;
  const outsCount = movesData?.outs?.moves?.length || 0;
  const totalEntries = Number(movesData?.entries?.total || 0);
  const totalOuts = Math.abs(Number(movesData?.outs?.total || 0));
  const balance = totalEntries - totalOuts;

  const rawInitial = closure?.initalValue ?? closure?.initialValue;
  const initialCash =
    rawInitial !== undefined && rawInitial !== null && Number(rawInitial) > 0
      ? Number(rawInitial)
      : movesData?.initialValue !== undefined && movesData?.initialValue !== null
      ? Number(movesData.initialValue)
      : cashier?.currentValue !== undefined && movesData?.totalBustling !== undefined
      ? Math.max(0, Number((Number(cashier.currentValue) - Number(movesData.totalBustling)).toFixed(2)))
      : Number(rawInitial || 0);

  const finalCash = closure?.finalValue !== undefined && closure?.finalValue !== null ? Number(closure.finalValue) : null;
  const saldoEmCaixa = (closure?.closed && finalCash !== null) ? finalCash : (initialCash + balance);

  const templateProps = {
    cashier,
    closure,
    movesData,
    paymentMethods,
    credencials,
    options: {
      printMode,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="lg"
      fullWidth
      classes={{
        paper: isMobile ? 'cashier-print-dialog-paper-mobile' : undefined,
      }}
    >
      <DialogTitle style={{ padding: isMobile ? '12px 14px' : '16px 24px', paddingBottom: 8 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <ReceiptIcon style={{ marginRight: 8, color: '#1B79DF' }} />
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} style={{ fontWeight: 'bold' }}>
              Imprimir Fechamento de Caixa
            </Typography>
            {cashier?.name && (
              <Chip
                label={cashier.name}
                size="small"
                color="primary"
                style={{ marginLeft: 8 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {onRefresh && (
              <Tooltip title="Sincronizar dados do caixa com a API">
                <span>
                  <IconButton
                    onClick={onRefresh}
                    disabled={loading}
                    size="small"
                    style={{ marginRight: 6 }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Painel Esquerdo: Opções de Configuração */}
          <Grid item xs={12} md={4}>
            {/* Opção: Modo de Exibição (Agrupado / Unitário / Completo) */}
            <Box mb={3}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" style={{ fontWeight: 'bold', color: '#1B79DF', marginBottom: 8 }}>
                  Modo de Exibição
                </FormLabel>
                <RadioGroup
                  name="print-mode"
                  value={printMode}
                  onChange={handlePrintModeChange}
                >
                  <FormControlLabel
                    value="agrupado"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center">
                        <ViewModuleIcon fontSize="small" style={{ marginRight: 6, color: '#555' }} />
                        <div>
                          <strong>Agrupado</strong>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Totais agrupados por método de pagamento
                          </Typography>
                        </div>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="unitario"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center">
                        <ViewListIcon fontSize="small" style={{ marginRight: 6, color: '#555' }} />
                        <div>
                          <strong>Unitário</strong>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Lançamento a lançamento detalhado
                          </Typography>
                        </div>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="completo"
                    control={<Radio color="primary" />}
                    label={
                      <Box display="flex" alignItems="center">
                        <DescriptionIcon fontSize="small" style={{ marginRight: 6, color: '#555' }} />
                        <div>
                          <strong>Completo</strong>
                          <Typography variant="caption" color="textSecondary" display="block">
                            Resumo agrupado + lista unitária
                          </Typography>
                        </div>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            {/* Opção: Formato de Impressão (Térmico 80mm / A4) */}
            <Box mb={3}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" style={{ fontWeight: 'bold', color: '#1B79DF', marginBottom: 8 }}>
                  Formato de Impressão
                </FormLabel>
                <RadioGroup
                  name="print-type"
                  value={printType}
                  onChange={handlePrintTypeChange}
                >
                  <FormControlLabel
                    value="thermal"
                    control={<Radio color="primary" />}
                    label={
                      <Box>
                        <strong>Cupom Térmico (80mm)</strong>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Impressora térmica de balcão / PDV
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="a4"
                    control={<Radio color="primary" />}
                    label={
                      <Box>
                        <strong>Folha A4</strong>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Relatório gerencial com cabeçalho
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            {/* Resumo Rápido em Cards */}
            <Box
              p={2}
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                Resumo do Caixa
              </Typography>
              {loading ? (
                <Box py={3} textAlign="center">
                  <CircularProgress size={24} style={{ color: '#1B79DF', marginBottom: 8 }} />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Sincronizando dados...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between" my={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      Fundo Inicial:
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#334155' }}>
                      {formattedCurrency(initialCash)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" my={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      Entradas ({entriesCount}x):
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#16a34a' }}>
                      {formattedCurrency(totalEntries)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" my={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      Saídas ({outsCount}x):
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#dc2626' }}>
                      -{formattedCurrency(totalOuts)}
                    </Typography>
                  </Box>
                  <Divider style={{ margin: '6px 0' }} />
                  <Box display="flex" justifyContent="space-between" my={0.5}>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      Balanço:
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 'bold',
                        color: balance >= 0 ? '#16a34a' : '#dc2626',
                      }}
                    >
                      {formattedCurrency(balance)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" my={0.5}>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {closure?.closed ? 'Saldo Final:' : 'Saldo Atual:'}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 'bold',
                        color: '#1e40af',
                      }}
                    >
                      {formattedCurrency(saldoEmCaixa)}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            <Box mt={2}>
              <Typography variant="caption" color="textSecondary" display="block">
                Suas preferências de modo e formato serão salvas automaticamente.
              </Typography>
            </Box>
          </Grid>

          {/* Painel Direito: Pré-visualização ao Vivo */}
          <Grid item xs={12} md={8}>
            {/* Header / Zoom Toolbar da Pré-visualização */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              mb={1}
              px={0.5}
            >
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 'bold', color: '#1B79DF' }}
              >
                Pré-visualização (
                {printType === 'thermal'
                  ? 'Cupom 80mm'
                  : `Folha A4 - ${Math.round(a4Scale * 100)}%`}
                )
              </Typography>

              {printType === 'a4' && (
                <Box display="flex" alignItems="center">
                  <ButtonGroup size="small" variant="outlined">
                    <Button
                      size="small"
                      onClick={() => setZoomMode('auto')}
                      variant={zoomMode === 'auto' ? 'contained' : 'outlined'}
                      color={zoomMode === 'auto' ? 'primary' : 'default'}
                      style={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        padding: '2px 8px',
                      }}
                      title="Ajustar à largura da tela"
                    >
                      <AspectRatioIcon
                        fontSize="small"
                        style={{ marginRight: 4 }}
                      />
                      Ajustar
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setZoomMode(0.5)}
                      variant={zoomMode === 0.5 ? 'contained' : 'outlined'}
                      color={zoomMode === 0.5 ? 'primary' : 'default'}
                      style={{ fontSize: '0.75rem', padding: '2px 6px' }}
                    >
                      50%
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setZoomMode(1.0)}
                      variant={zoomMode === 1.0 ? 'contained' : 'outlined'}
                      color={zoomMode === 1.0 ? 'primary' : 'default'}
                      style={{ fontSize: '0.75rem', padding: '2px 6px' }}
                      title="Tamanho real (100%)"
                    >
                      100%
                    </Button>
                  </ButtonGroup>
                </Box>
              )}
            </Box>

            <div
              ref={containerRef}
              className="cashier-print-preview-container"
            >
              {loading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  p={4}
                  minHeight={360}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    width: '100%',
                    maxWidth: 420,
                    margin: '30px auto',
                    textAlign: 'center',
                  }}
                >
                  <CircularProgress
                    size={44}
                    thickness={4}
                    style={{ color: '#1B79DF', marginBottom: 16 }}
                  />
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600, color: '#1e293b', marginBottom: 6 }}
                  >
                    Atualizando dados do caixa...
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ maxWidth: 320 }}
                  >
                    Buscando os lançamentos mais recentes na API para garantir que o resumo impresso seja 100% fiel.
                  </Typography>
                </Box>
              ) : printType === 'thermal' ? (
                <div
                  className="cashier-print-preview-card"
                  style={{
                    width: '80mm',
                    minWidth: '80mm',
                    maxWidth: '100%',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    backgroundColor: '#fff',
                    pointerEvents: 'none',
                  }}
                >
                  <CashierThermalTemplate {...templateProps} />
                </div>
              ) : (
                <div
                  style={{
                    width: Math.round(794 * a4Scale),
                    height: a4Height ? Math.round(a4Height * a4Scale) : undefined,
                    overflow: 'hidden',
                    margin: '0 auto',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    borderRadius: 4,
                    backgroundColor: '#fff',
                    transition: 'width 0.15s ease, height 0.15s ease',
                  }}
                >
                  <div
                    ref={a4ContentRef}
                    style={{
                      width: '794px',
                      minWidth: '794px',
                      maxWidth: '794px',
                      transform: `scale(${a4Scale})`,
                      transformOrigin: 'top left',
                      pointerEvents: 'none',
                    }}
                  >
                    <CashierA4Template {...templateProps} />
                  </div>
                </div>
              )}
            </div>
          </Grid>
        </Grid>

        {/* Elemento Oculto para react-to-print */}
        {!loading && (
          <div className="cashier-print-content-hidden">
            <div
              ref={printRef}
              className={
                printType === 'thermal'
                  ? 'cashier-print-wrapper-thermal'
                  : 'cashier-print-wrapper-a4'
              }
            >
              {printType === 'thermal' ? (
                <CashierThermalTemplate {...templateProps} />
              ) : (
                <CashierA4Template {...templateProps} />
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={handleClose} color="default" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handlePrint}
          color="primary"
          variant="contained"
          size="large"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <PrintIcon />
            )
          }
          autoFocus
        >
          {loading ? 'Atualizando...' : 'Imprimir Agora'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashierPrintPreviewModal;
